import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import TagCategoryService from "services/TagCategory";
import TagService from "services/Tag";
import tagConstants from "constants/Tag";
import {
  getTagManagementTableColumns,
  getTagManagementTableFilters,
} from "routes/tags/TagManagementTableColumnsAndFilters";
import { TAG_MANAGEMENT_TABLE_CONSTANTS } from "routes/tags/TagManagementTableV3";
import UserService from "services/User";

export const invalidateTagsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [TAG_MANAGEMENT_TABLE_CONSTANTS.queryKey],
  });
};

export const useTagMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => TagService.createTag(data),
    onSuccess: () => invalidateTagsQuery(),
    onError: err => toast.error(formatError(err, "Failed to save tag")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => TagService.editTag(id, data),
    onSuccess: (_data, { id }) => {
      invalidateTagsQuery();
      queryClient.invalidateQueries({ queryKey: ["tag", id] });
    },
    onError: err => toast.error(formatError(err, "Failed to save tag")),
  });

  const remove = useMutation({
    mutationFn: id => TagService.deleteTag(id),
    onSuccess: () => {
      invalidateTagsQuery();
      toast.success("Tag deleted");
      onDeleted?.();
    },
    onError: err => toast.error(formatError(err, "Failed to delete")),
  });

  const bulkRemove = useMutation({
    mutationFn: ids => TagService.bulkDeleteTags(ids),
    onSuccess: (_data, ids) => {
      invalidateTagsQuery();
      toast.success(`${ids.length} tag(s) deleted`);
      onDeleted?.();
    },
    onError: err => toast.error(formatError(err, "Failed to delete")),
  });

  const bulkUpdateCategory = useMutation({
    mutationFn: ({ categoryId, tagIds }) =>
      TagService.bulkUpdateCategory(categoryId, tagIds),
    onSuccess: () => invalidateTagsQuery(),
    onError: err => toast.error(formatError(err, "Failed to update category")),
  });

  return { create, update, remove, bulkRemove, bulkUpdateCategory };
};

const handleFetchTagCategories = async () => {
  const tagCategories = await TagCategoryService.getTagCategoryForFilters();
  return tagCategories;
};

export const useTagManagementTable = ({
  canEditTag = false,
  canDeleteTag = false,
  onEditTag = () => {},
  onCloneTag = () => {},
  onDeleteTag = () => {},
}) => {
  const { data: tagCategories, isSuccess: isTagCategoriesFetched } = useQuery({
    queryKey: ["tag-categories"],
    queryFn: handleFetchTagCategories,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const taggableModels = useMemo(() => {
    return Object.entries(tagConstants.taggableModels).map(([key, value]) => ({
      id: key,
      name: value,
    }));
  }, []);

  const filters = useMemo(() => {
    if (!isTagCategoriesFetched) {
      return [];
    }

    return getTagManagementTableFilters({
      tagCategories: tagCategories || [],
      taggableModels,
    });
  }, [tagCategories, taggableModels, isTagCategoriesFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];

    const initialColumns = getTagManagementTableColumns({
      canEditTag,
      canDeleteTag,
      onEditTag,
      onCloneTag,
      onDeleteTag,
    });

    const tagTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${TAG_MANAGEMENT_TABLE_CONSTANTS.tableName}`,
      {}
    );

    // Apply resized columns if they exist
    const resizedColumns = tagTableSettings?.resized_columns || {};
    const columnsWithResize = initialColumns.map(col => {
      if (resizedColumns[col.id]) {
        return {
          ...col,
          size: resizedColumns[col.id],
        };
      }
      return col;
    });

    return columnsWithResize;
  }, [
    tableSettings,
    isTableSettingsFetched,
    canEditTag,
    canDeleteTag,
    onEditTag,
    onCloneTag,
    onDeleteTag,
  ]);

  return {
    filters,
    columns,
    tagCategories,
    taggableModels,
  };
};
